/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import { createTheme, type Theme, type ThemeOptions } from '@mui/material';
import { colors } from '../utils/colors';
import {
  fontSize,
  regularTextFontFamily,
  titleTextFontFamily,
} from '../utils/fonts';

const mainColor = colors.blue;
const secondaryColor = colors.veniceBlue;

const defBreakpoints: ThemeOptions['breakpoints'] = {
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
  },
};

const defTypography: ThemeOptions['typography'] = {
  htmlFontSize: fontSize,
};

const defCustomShadows: Theme['customShadows'] = {
  light: '0 0 11px 0 rgba(63, 73, 105, 0.05)',
  medium: '0 0 11px 0 rgba(63, 73, 105, 0.15)',
  strong: '5px 5px 10px 0 rgba(0, 0, 0, 0.4)',
  scrollbar: '0 0 1px rgba(255, 255, 255, 0.5)',
};

const {
  breakpoints,
  spacing,
  typography: { pxToRem },
} = createTheme({
  breakpoints: defBreakpoints,
  typography: defTypography,
  customShadows: defCustomShadows,
});

const componentsData = {
  paper: {
    borderRadius: {
      desktop: 18,
      mobile: 12,
    },
  },
};

export const theme = createTheme({
  breakpoints: defBreakpoints,
  typography: {
    htmlFontSize: fontSize,
    fontFamily: regularTextFontFamily,
    h1: {
      fontSize: pxToRem(26),
      lineHeight: 1.32,
      fontFamily: titleTextFontFamily,
    },
    h2: {
      fontSize: pxToRem(24),
      lineHeight: 1.32,
      fontWeight: 600,
      fontFamily: titleTextFontFamily,
    },
    h3: {
      fontSize: pxToRem(18),
      lineHeight: 1.2,
      fontWeight: 600,
      fontFamily: titleTextFontFamily,
      [breakpoints.down('sm')]: {
        fontSize: pxToRem(20),
      },
    },
    h4: {
      fontSize: pxToRem(18),
      lineHeight: 1.2,
      fontWeight: 600,
      [breakpoints.down('sm')]: {
        fontSize: pxToRem(20),
      },
    },
    subtitle1: {
      // fontSize: '1.1428571428571428rem', // default
      // lineHeight: 1.75,
      fontWeight: 600,
    },
    subtitle2: {
      fontSize: pxToRem(16),
      lineHeight: 1.5,
      fontWeight: 600,
    },
    body1: {
      fontSize: pxToRem(16),
      [breakpoints.down('sm')]: {
        fontSize: pxToRem(17),
      },
    },
  },
  customShadows: defCustomShadows,
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          subtitle2: 'p',
        },
      },
      styleOverrides: {
        root: {
          color: colors.darkBlue,
        },
        h1: {
          color: secondaryColor,
          '&.MuiTypography-gutterBottom': {
            marginBottom: spacing(4.25),
            [breakpoints.down('sm')]: {
              marginBottom: spacing(2.5),
            },
          },
        },
        h2: {
          color: secondaryColor,
          '&.MuiTypography-gutterBottom': {
            marginBottom: spacing(2.5),
            [breakpoints.down('sm')]: {
              marginBottom: spacing(1.5),
            },
          },
        },
        h3: {
          color: secondaryColor,
          '&.MuiTypography-gutterBottom': {
            marginBottom: spacing(2),
            [breakpoints.down('sm')]: {
              marginBottom: spacing(1.25),
            },
          },
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
      styleOverrides: {
        root: {
          fontFamily: regularTextFontFamily,
          cursor: 'pointer',
          color: mainColor,
          '&:hover': {
            color: secondaryColor,
            textDecoration: 'none',
          },
        },
        underlineAlways: {
          '&:hover': {
            textDecoration: 'none',
          },
        },
      },
    },
    MuiCheckbox: {
      defaultProps: { color: 'primary' },
      styleOverrides: {
        root: {
          width: 26,
          height: 26,
          padding: 4,
          marginLeft: -4,
          '& .MuiSvgIcon-root': {
            fontSize: '1.5rem',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: { variant: 'contained', fullWidth: true },
      styleOverrides: {
        root: {
          fontSize: pxToRem(17),
          lineHeight: 1.2,
          fontWeight: 600,
          textTransform: 'none',
        },
        sizeSmall: {
          // font-size: 0.9285714285714286rem; default
          // padding: 4px 10px; default
        },
        contained: {
          padding: '15px 24px 17px',
          borderRadius: 60,
          color: colors.white,
          '&.MuiButton-containedError': {
            backgroundColor: colors.flamingo,
            '&:hover': {
              backgroundColor: secondaryColor,
              boxShadow: 'none',
              '@media (hover: none)': {
                backgroundColor: secondaryColor,
                boxShadow: 'none',
              },
            },
          },
          [breakpoints.down('sm')]: {
            padding: '17px 24px 19px',
            lineHeight: 1.2615,
          },
        },
        containedSizeSmall: {
          padding: '10px 10px',
          fontSize: '1rem', // 14px
        },
        containedPrimary: {
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: secondaryColor,
            boxShadow: 'none',
            color: colors.white,
            '@media (hover: none)': {
              backgroundColor: secondaryColor,
              boxShadow: 'none',
              color: colors.white,
            },
          },
        },
        outlined: {
          padding: '15px 15px',
          borderRadius: 60,
        },
        outlinedPrimary: {
          borderColor: colors.blue,
          color: mainColor,
          '&:hover': {
            backgroundColor: 'transparent',
            borderColor: secondaryColor,
            color: secondaryColor,
            '@media (hover: none)': {
              backgroundColor: 'transparent',
              color: secondaryColor,
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          width: 38,
          height: 38,
        },
        sizeSmall: {
          width: 20,
          height: 20,
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
        square: false,
      },
      styleOverrides: {
        rounded: {
          borderRadius: componentsData.paper.borderRadius.desktop,
          [breakpoints.down('md')]: {
            borderRadius: componentsData.paper.borderRadius.mobile,
          },
        },
        elevation1: {
          boxShadow: defCustomShadows.light,
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(10, 53, 90, 0.7)',
          backdropFilter: 'blur(4px)',
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          '& .MuiPaper-root': {
            'overflow-y': 'visible',
          },
          [breakpoints.down('sm')]: {
            '&:not(.MuiDrawer-modal) .MuiPaper-root': {
              margin: 14,
              maxHeight: 'calc(100% - 28px)',
            },
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        marginNormal: {
          marginTop: 12,
          marginBottom: 8,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: colors.darkBlue,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          '&.Mui-error': {
            fontWeight: 600,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            display: 'block !important',
          },
        },
        outlined: {
          color: colors.boulder,
          // transform: 'translate(14px, 18px) scale(1)',
          /*'&$shrink': {
            transform: 'translate(14px, -6px) scale(1)',
          },*/
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        margin: 'normal',
        variant: 'outlined',
        fullWidth: true,
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          paddingTop: 16.5,
        },
        notchedOutline: {
          borderRadius: 3,
        },
      },
    },
    MuiRadio: {
      defaultProps: { color: 'primary' },
    },
    MuiSelect: {
      defaultProps: {
        variant: 'outlined',
        MenuProps: {
          slotProps: {
            paper: {
              sx: {
                padding: '6px 10px',
                '& .MuiList-root': {
                  padding: 0,
                },
                '& .MuiMenuItem-root': {
                  padding: 1,
                  fontWeight: 400,
                  '&:hover': {
                    backgroundColor: colors.hawkesBlue,
                  },
                },
                '& .MuiMenuItem-root + .MuiMenuItem-root': {
                  borderTop: `1px solid ${colors.mercury}`,
                },
              },
            },
          },
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: '#fff',
          border: `1px solid ${colors.gray}`,
          color: colors.black,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          color: colors.darkBlue,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: '6px 16px',
          color: mainColor,
          fontWeight: 600,
          transition: 'color 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'transparent',
            color: colors.darkBlue,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: colors.darkBlue,
        },
      },
    },
  },
  palette: {
    colors,
    primary: {
      main: mainColor,
    },
    // Should be replaced on refactor
    secondary: {
      main: mainColor,
    },
  },
  mixins: {
    toolbar: {
      minHeight: 'auto',
      height: 50,
      [breakpoints.down('md')]: {
        height: 60,
      },
    },
  },
});
